import { ReactNode } from "react";
import { useNavigate } from "react-router";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import {
  PRIVATE_MODE_SX,
  usePatientPrivacy
} from "hooks/use-patient-privacy.hook";
import { TinyInsurancePolicy } from "services/types/insurance-policies.type";
import { ManagedCarePlan } from "services/types/managed-care-plans.type";
import { Patient } from "services/types/patients.type";

import AdditionalBalanceDisplay from "./additional-balance-display.component";
import BalanceExpander from "./balance-expander.component";
import PatientResponsibilityBalanceDisplay from "./patient-responsibility-balance-display.component";
import useBalanceDisplay from "./use-balance-display.hook";

type BalanceDisplayProps = {
  patient: Patient;
  totalBalance: number;
  insurerResponsibilityBalance: number;
  patientResponsibilityBalance: number;
  managedCareResponsibilityBalance: number;
  managedCarePlan?: ManagedCarePlan;
  insurancePolicy?: TinyInsurancePolicy;
  paymentSourceType: number;
  showDecimal?: boolean;
  prefixText?: string;
  adornment?: ReactNode;
  sx?: SxProps;
};
export const BALANCE_PRIVATE_MODE_SX = PRIVATE_MODE_SX;
export default function BalanceDisplay({
  patient,
  totalBalance,
  insurerResponsibilityBalance,
  patientResponsibilityBalance,
  managedCareResponsibilityBalance,
  managedCarePlan,
  insurancePolicy,
  paymentSourceType,
  showDecimal = false,
  prefixText = "",
  adornment = <></>,
  sx = {}
}: BalanceDisplayProps) {
  const navigate = useNavigate();

  const navigateToBilling = () => {
    navigate(`/patients/${patient?.id}/billing`);
  };

  const {
    totalBalanceColor,
    totalBalanceText,
    patientResponsibilityBalanceColor,
    patientResponsibilityBalanceText,
    additionalBalance,
    handleShowBalanceExpander,
    handleHideBalanceExpander,
    balanceExpanderContent,
    balanceAnchorEl
  } = useBalanceDisplay({
    totalBalance,
    insurerResponsibilityBalance,
    patientResponsibilityBalance,
    managedCareResponsibilityBalance,
    managedCarePlan,
    insurancePolicy,
    paymentSourceType,
    showDecimal,
    prefixText
  });
  const { isPrivateMode } = usePatientPrivacy();
  return (
    <Stack
      data-testid={`sale-balance-color-${totalBalanceColor}`}
      onClick={navigateToBilling}
      sx={{
        ml: 3.5,
        mr: 0.5,
        mb: 1,
        pt: 0.25,
        pb: 1,
        px: 2,
        background: totalBalanceColor,
        cursor: "pointer",
        borderRadius: "5px",
        display: "flex",
        maxWidth: "220px",
        boxSizing: "border-box",
        boxShadow: "2px 2px 2px 0px rgba(0,0,0,0.15)",
        ...sx
      }}
    >
      <Stack direction="row" alignItems="center">
        <Box sx={{ width: 40, height: 40 }}>{adornment}</Box>
        <Typography
          fontWeight="700"
          variant="h5"
          fontSize="23px"
          data-testid="sale-balance-value"
          data-color={totalBalanceColor}
          textAlign="right"
          width="100%"
          sx={isPrivateMode ? BALANCE_PRIVATE_MODE_SX : {}}
        >
          {totalBalanceText}
        </Typography>
      </Stack>
      <AdditionalBalanceDisplay
        isPrivateMode={isPrivateMode}
        totalBalanceColor={totalBalanceColor}
        label={additionalBalance.label}
        value={additionalBalance.value}
        color={additionalBalance.color}
      />
      <PatientResponsibilityBalanceDisplay
        isPrivateMode={isPrivateMode}
        totalBalanceColor={totalBalanceColor}
        color={patientResponsibilityBalanceColor}
        expanderContent={balanceExpanderContent}
        anchorEl={balanceAnchorEl}
        text={patientResponsibilityBalanceText}
        handleShowBalanceExpander={handleShowBalanceExpander}
        handleHideBalanceExpander={handleHideBalanceExpander}
      />

      {Boolean(balanceExpanderContent) && (
        <BalanceExpander
          open={Boolean(balanceAnchorEl)}
          anchorEl={balanceAnchorEl}
          isPrivateMode={isPrivateMode}
          onClose={handleHideBalanceExpander}
        >
          {balanceExpanderContent}
        </BalanceExpander>
      )}
    </Stack>
  );
}
